<template>
  <div v-if="visible" class="agent-panel-host" :class="`agent-panel-host--${panelState}`">
    <AgentChatPanel v-model:state="panelState" :agent-name="agentName" :messages="messages" :invitation="invitation"
      :responding="responding" :unread="unread" :disabled="responding" :completed="completed" @send="send" />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import AgentChatPanel from '~/components/portal/ai/AgentChatPanel.vue'
import {
  useBorrowerAgent,
  PANEL_EXPANDED,
  PANEL_COLLAPSED,
  PANEL_MINIMIZED,
} from '~/composables/useBorrowerAgent'

/**
 * Mounts an agent chat panel portal-wide and owns its conversation.
 * Lives in the layout so panel + conversation survive navigation.
 */
const props = defineProps({
  /**
   * Agent key as it appears in the endpoint path, e.g. 'merchant-interview'.
   */
  agentKey: {
    type: String,
    required: true,
  },
  agentName: {
    type: String,
    default: 'Lendio Assistant',
  },
  /**
   * Opening copy, chosen by the mount from the eligibility response's templateAlias + anchorFacts.
   */
  invitationFor: {
    type: Function,
    default: () => '',
  },
})

const emit = defineEmits(['impression', 'collapsed', 'minimized', 'engaged', 'completed', 'abandoned', 'expandedChange'])

// px scrolled before an unengaged panel demotes to the pill
const SCROLL_DEMOTE_PX = 400

// idle ms before an unengaged panel demotes
const IDLE_DEMOTE_MS = 30_000

// min ms the typing indicator shows per turn
const MIN_TYPING_MS = 900

const {
  borrowerId,
  getStoredSession,
  saveSession,
  getStoredPanelState,
  savePanelState,
  resolveEligibilityOnce,
  startSession,
  continueSession,
} = useBorrowerAgent(props.agentKey)

const visible = ref(false)
const panelState = ref(PANEL_EXPANDED)
const messages = ref([])
const invitation = ref('')
const chatSessionId = ref(null)
const responding = ref(false)
const unread = ref(false)
const completed = ref(false)

let idleTimer = null

// one abandoned signal per walk-away; a new message re-arms it
let abandonSignaled = false

// marks the next state change as auto-demote, not a user action
let selfDemote = false

// borrower has said something — the panel stops getting out of the way
const engaged = computed(() => messages.value.length > 0)

// the mount hides the Intercom launcher while the panel occupies its corner
const expandedVisible = computed(() => visible.value && panelState.value === PANEL_EXPANDED)

watch(expandedVisible, (value) => emit('expandedChange', value))

async function init() {
  const id = borrowerId.value

  if (!id) return

  try {
    const { eligible, templateAlias, anchorFacts } = await resolveEligibilityOnce(id)

    // borrower switched mid-flight — a newer init owns the panel now
    if (borrowerId.value !== id) return

    if (!eligible) return

    visible.value = true
    invitation.value = props.invitationFor(templateAlias, anchorFacts ?? null)

    const stored = getStoredSession(id)

    if (stored) {
      chatSessionId.value = stored.chatSessionId
      messages.value = stored.messages ?? []
      completed.value = stored.completed === true
    }

    // stored state = this visit already saw the panel; respect it
    const storedState = getStoredPanelState(id)

    if (storedState) {
      // restoring a collapsed mid-interview session isn't a new walk-away
      abandonSignaled = storedState !== PANEL_EXPANDED
      panelState.value = storedState
    } else {
      panelState.value = PANEL_EXPANDED
      unread.value = true
      emit('impression')
    }

    startIdleTimer()
  } catch {
    if (borrowerId.value === id) visible.value = false
  }
}

// a business switch changes the borrower w/out remounting — drop the old conversation
function reset() {
  clearTimeout(idleTimer)
  abandonSignaled = false
  visible.value = false
  messages.value = []
  invitation.value = ''
  chatSessionId.value = null
  responding.value = false
  unread.value = false
  completed.value = false
}

async function send(text) {
  const id = borrowerId.value

  if (!text || responding.value || completed.value || !id) return

  const first = !chatSessionId.value

  messages.value.push({ role: 'user', text })
  panelState.value = PANEL_EXPANDED
  responding.value = true
  abandonSignaled = false

  if (messages.value.length === 1) emit('engaged')

  const typingStartedAt = Date.now()
  const typingRemainder = () => new Promise((resolve) => {
    setTimeout(resolve, Math.max(0, MIN_TYPING_MS - (Date.now() - typingStartedAt)))
  })

  try {
    // 409 (already complete) resolves as complete:true via the composable
    let result = first
      ? await startSession(id, text)
      : await continueSession(id, chatSessionId.value, text)

    // idempotent start: an active session returns w/out processing the message — replay it
    if (first && result?.chatSessionId && result?.lastMessage === null && !result?.complete) {
      result = await continueSession(id, result.chatSessionId, text)
    }

    // borrower switched mid-flight — this reply belongs to the old conversation
    if (borrowerId.value !== id) return

    chatSessionId.value = result?.chatSessionId ?? chatSessionId.value

    await typingRemainder()

    if (result?.lastMessage) {
      messages.value.push({ role: 'agent', text: result.lastMessage })
    }

    if (result?.complete && !completed.value) {
      completed.value = true
      emit('completed')
    }

    saveSession(id, chatSessionId.value, messages.value, completed.value)
  } catch {
    if (borrowerId.value !== id) return

    await typingRemainder()

    messages.value.push({
      role: 'agent',
      text: 'Something went wrong on our end. Please try again.',
    })
  } finally {
    responding.value = false
  }
}

// two-stage: expanded → bar → pill, so the panel recedes instead of vanishing
function demote() {
  // the scroll listener outlives visibility — a hidden panel has nothing to demote
  if (!visible.value) return
  if (engaged.value || responding.value) return
  if (panelState.value === PANEL_MINIMIZED) return

  selfDemote = true

  if (panelState.value === PANEL_EXPANDED) {
    panelState.value = PANEL_COLLAPSED
    startIdleTimer()
  } else {
    panelState.value = PANEL_MINIMIZED
    emit('minimized')
  }
}

function onScroll() {
  // scroll only demotes the expanded panel — the bar is already out of the way
  if (panelState.value !== PANEL_EXPANDED) return
  if (window.scrollY > SCROLL_DEMOTE_PX) demote()
}

function startIdleTimer() {
  clearTimeout(idleTimer)
  idleTimer = setTimeout(demote, IDLE_DEMOTE_MS)
}

watch(panelState, (next, previous) => {
  const id = borrowerId.value

  if (id) savePanelState(id, next)

  if (next === PANEL_EXPANDED) {
    unread.value = false
    startIdleTimer()
  }

  // user collapse only — a self-demote isn't the same signal
  if (
    next === PANEL_COLLAPSED
    && previous === PANEL_EXPANDED
    && !selfDemote
  ) {
    emit('collapsed')
  }

  selfDemote = false

  // auto-demote never fires while engaged, so leaving expanded mid-interview is deliberate
  if (
    next !== PANEL_EXPANDED
    && engaged.value
    && !completed.value
    && !abandonSignaled
  ) {
    abandonSignaled = true
    emit('abandoned')
  }
})

watch(borrowerId, (id, previous) => {
  if (previous) reset()
  if (id) init()
}, { immediate: true })

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  clearTimeout(idleTimer)
})
</script>

<style scoped lang="scss">
.agent-panel-host {
  position: fixed;
  z-index: 1040;

  &--expanded {
    top: 0.75rem;
    right: 0.75rem;
    bottom: 0.75rem;
    width: 380px;

    @include media-breakpoint-down(md) {
      // top: auto makes the height indefinite — flex lets the panel shrink to the cap
      display: flex;
      flex-direction: column;
      top: auto;
      width: auto;
      left: 0.75rem;
      right: 0.75rem;
      bottom: 4.5rem;
      max-height: 70vh;
    }
  }

  &--collapsed {
    left: 50%;
    bottom: 1.5rem;
    transform: translateX(-50%);
    width: min(32rem, calc(100vw - 1.5rem));
  }

  &--minimized {
    left: 50%;
    bottom: 1.5rem;
    transform: translateX(-50%);
  }
}
</style>
